import { useBooleanFlagValue } from '@openfeature/react-sdk';
import ButtonDropdown from '@risk-smart/themed-cloudscape-components/button-dropdown';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { type FC, type Ref, useCallback, useEffect, useState } from 'react';
import {
  FormEditorDialog,
  type FormEditorOutput,
  type FormEditorPreviewData,
  type FormPreviewHandle,
} from 'src/blocks/form-editor-dialog';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useObjectToolbarFormStore } from '@/components/object-toolbar/useObjectToolbarFormStore';
import { useCanManageFormConfig } from '@/hooks/queries/form-configuration/useCanManageFormConfig';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useEditableFormContext } from './customisable-form/EditableFormContext';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { LiveFormPreview } from './LiveFormPreview';
import { useFormEditorConditionalOptions } from './useFormEditorConditionalOptions';
import { useFormEditorInitialData } from './useFormEditorInitialData';
import { useFormEditorUpdate } from './useFormEditorUpdate';

interface FormMenuProps {
  parentType: FormId;
}
export const FormMenu: FC<FormMenuProps> = ({ parentType }) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const objectToolbarEnabled = useBooleanFlagValue('object_toolbar', false);
  const { canManage, loading: rbacLoading } =
    useCanManageFormConfig(parentType);
  const { hasPermission, loading: legacyLoading } = useHasPermissionQuery(
    'update:custom_attribute_schema'
  );

  const canShowMenu = trpcEnabled ? canManage : hasPermission;
  const loading = trpcEnabled ? rbacLoading : legacyLoading;

  // When both the object toolbar and form sections are live, the page-header
  // toolbar takes over: its overflow "Edit" group exposes "Edit form", so the
  // 3-dot dropdown here is suppressed and only the form editor it opens remains.
  const relocateToToolbar =
    objectToolbarEnabled && formSectionsEnabled && canShowMenu && !loading;

  const { addNotification } = useNotifications();
  const { toggleEditMode } = useRiskSmartForm();
  const { showEditModal } = useEditableFormContext();

  const [configureOpen, setConfigureOpen] = useState(false);
  const setFormConfigState = useObjectToolbarFormStore(
    (s) => s.setFormConfigState
  );
  const clearFormConfigState = useObjectToolbarFormStore(
    (s) => s.clearFormConfigState
  );

  // Bridge the form editor opener up to the page-header toolbar (mounted outside
  // this provider). Published only in relocate mode; cleared on unmount / when
  // the flags or permission change, so a non-relocating FormMenu leaves the slot
  // null and the toolbar shows no "Edit form" action.
  useEffect(() => {
    if (!relocateToToolbar) {
      return;
    }
    setFormConfigState({ openConfigure: () => setConfigureOpen(true) });

    return () => clearFormConfigState();
  }, [relocateToToolbar, setFormConfigState, clearFormConfigState]);

  const { customAttributeSchema, formFieldConfigurations } =
    useCustomisableFormDataContext();

  const { initialData, fieldTypeOptions } =
    useFormEditorInitialData(parentType);

  const {
    fieldOptions: conditionalFieldOptions,
    getValueOptions,
    getFieldTypeMetadata,
  } = useFormEditorConditionalOptions(parentType, configureOpen);

  const save = useFormEditorUpdate({
    parentType,
    initialData,
    customAttributeSchema,
    formFieldConfigurations,
  });
  const [saving, setSaving] = useState(false);

  const onConfigureSave = (data: FormEditorOutput) => {
    // Set saving synchronously so the button disables on the next render
    // before any async work starts. Use .then()/.catch() instead of
    // async/await to keep this handler synchronous.
    setSaving(true);
    save(data)
      .then(() => {
        setConfigureOpen(false);
      })
      .catch(() => {
        addNotification({
          type: 'error',
          content: 'Failed to save form configuration. Please try again.',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const renderPreview = useCallback(
    (
      data: FormEditorPreviewData,
      opts: { ref: Ref<FormPreviewHandle>; onDataChange: () => void }
    ) => (
      <LiveFormPreview
        ref={opts.ref}
        data={data}
        onDataChange={opts.onDataChange}
      />
    ),
    []
  );

  const onAddFieldAction = () => {
    showEditModal({
      parentType: parentType,
      fieldId: undefined,
      defaultRequired: undefined,
      defaultValueOptions: [],
      allowDefaultValue: true,
      forceRequired: undefined,
    });
  };

  const onEditAction = () => {
    toggleEditMode();
  };

  if (!canShowMenu || loading) {
    return null;
  }

  return (
    <>
      {relocateToToolbar ? null : (
        <ButtonDropdown
          data-testid={'form-settings-button'}
          expandToViewport
          ariaLabel={'Form Settings'}
          items={[
            ...(formSectionsEnabled
              ? [
                  {
                    // TODO: translation
                    text: 'Configure form',
                    id: 'configure',
                    disabled: false,
                  },
                ]
              : []),
            {
              // TODO: translation
              text: 'Add custom field',
              id: 'add-field',
              disabled: false,
            },
            {
              // TODO: translation
              text: 'Edit form',
              id: 'edit',
              disabled: false,
            },
          ]}
          variant={'icon'}
          onItemClick={(e) => {
            if (e.detail.id === 'configure') {
              setConfigureOpen(true);
            }
            if (e.detail.id === 'add-field') {
              onAddFieldAction();
            }
            if (e.detail.id === 'edit') {
              onEditAction();
            }
          }}
        >
          {'...'}
        </ButtonDropdown>
      )}
      {configureOpen ? (
        <FormEditorDialog
          open={configureOpen}
          onOpenChange={setConfigureOpen}
          initialData={initialData}
          fieldOptions={conditionalFieldOptions}
          getValueOptions={getValueOptions}
          getFieldTypeMetadata={getFieldTypeMetadata}
          onSave={onConfigureSave}
          saving={saving}
          renderPreview={renderPreview}
          fieldTypeOptions={fieldTypeOptions}
          lang={
            formSectionsEnabled
              ? undefined
              : { formEditor: { addSection: undefined } }
          }
        />
      ) : null}
    </>
  );
};
